
import { DogSchool } from "@/data/dogSchools";
import { dogSchools } from "@/data/dogSchools";
import { supabase } from "@/integrations/supabase/client";
import { mapSupabaseRowToDogSchool } from "@/utils/supabaseMappers";
import { fetchDogSchoolsByState } from "../state/stateService";
import { FilterParams } from "../utils/filterUtils";
import { sortSchoolsByReviewCount } from "../utils/sortUtils";

// Enhanced cache for state requests with improved invalidation strategy
const stateSchoolsCache: Record<string, {
  timestamp: number;
  schools: DogSchool[];
  hasMore: boolean;
  totalCount?: number;
}> = {};

// Improved cache validation helper function
const isCacheValid = (cacheKey: string, cacheExpiration: number = 5 * 60 * 1000) => {
  return (
    stateSchoolsCache[cacheKey] && 
    Date.now() - stateSchoolsCache[cacheKey].timestamp < cacheExpiration
  );
};

/**
 * Cache invalidation function for specific state
 */
const invalidateStateCache = (country: string, state: string) => {
  // Remove all cache entries related to this country and state
  const keysToRemove: string[] = [];
  
  Object.keys(stateSchoolsCache).forEach(key => {
    if (key.includes(`${country}-${state}`)) {
      keysToRemove.push(key);
    }
  });
  
  keysToRemove.forEach(key => {
    delete stateSchoolsCache[key];
  });
  
  console.log(`Invalidated ${keysToRemove.length} cache entries for ${country}-${state}`);
};

/**
 * Optimized function to fetch paginated dog schools by state with improved caching
 */
export const fetchPaginatedDogSchoolsByState = async (
  country: string,
  state: string,
  page: number = 0, 
  pageSize: number = 30  // Reduce default page size for faster initial loading
): Promise<{
  schools: DogSchool[];
  hasMore: boolean;
  totalCount?: number;
}> => {
  // Normalize state name for consistent caching
  const normalizedState = state.toLowerCase().trim();
  const cacheKey = `${country}-${normalizedState}-${page}-${pageSize}`;
  const firstPageCacheKey = `${country}-${normalizedState}-0-${pageSize}`;
  const cacheExpiration = 5 * 60 * 1000; // 5 minutes
  
  // Log cache access
  console.log(`Checking cache for key: ${cacheKey}`);
  
  // Check if we have a cached result that's still valid
  if (isCacheValid(cacheKey, cacheExpiration)) {
    console.log("Using cached result for state schools");
    return {
      schools: stateSchoolsCache[cacheKey].schools,
      hasMore: stateSchoolsCache[cacheKey].hasMore,
      totalCount: stateSchoolsCache[cacheKey].totalCount
    };
  }
  
  try {
    // Normalize country code to full name
    const normalizedCountry = country === 'de' ? 'Germany' : 
                            country === 'at' ? 'Austria' : 
                            country === 'ch' ? 'Switzerland' : country;
    
    // First, check if we have the total count cached already to avoid redundant queries
    let totalCount: number | undefined;
    if (isCacheValid(firstPageCacheKey, cacheExpiration)) {
      totalCount = stateSchoolsCache[firstPageCacheKey].totalCount;
    }
    
    // Optimize: If we're requesting any page and we already know there are no results, return early
    if (totalCount === 0) {
      return { schools: [], hasMore: false, totalCount: 0 };
    }
    
    console.log(`Fetching schools for ${normalizedCountry}, state: ${normalizedState}`);
    
    // Performance optimization: Fetch all schools for this state
    const allSchools = await fetchDogSchoolsByState(country, normalizedState);
    
    // If no schools found, try with alternative state name format
    if (allSchools.length === 0) {
      // Try with hyphens replaced by spaces or vice versa
      const alternativeState = normalizedState.includes('-') 
        ? normalizedState.replace(/-/g, ' ') 
        : normalizedState.replace(/ /g, '-');
      
      console.log(`No schools found for '${normalizedState}', trying alternative format: '${alternativeState}'`);
      
      const alternativeSchools = await fetchDogSchoolsByState(country, alternativeState);
      
      if (alternativeSchools.length > 0) {
        console.log(`Found ${alternativeSchools.length} schools with alternative state name format`);
        
        // Calculate pagination
        const startIndex = page * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedSchools = alternativeSchools.slice(startIndex, endIndex);
        const hasMore = endIndex < alternativeSchools.length;
        
        // Cache the result with both original and alternative state name format
        const result = {
          schools: paginatedSchools,
          hasMore,
          totalCount: alternativeSchools.length
        };
        
        stateSchoolsCache[cacheKey] = {
          timestamp: Date.now(),
          ...result
        };
        
        // Also cache the first page for reference
        if (page === 0 || !isCacheValid(firstPageCacheKey, cacheExpiration)) {
          stateSchoolsCache[firstPageCacheKey] = {
            timestamp: Date.now(),
            schools: alternativeSchools.slice(0, pageSize),
            hasMore: pageSize < alternativeSchools.length,
            totalCount: alternativeSchools.length
          };
        }
        
        return result;
      }
    }
    
    // Calculate pagination
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedSchools = allSchools.slice(startIndex, endIndex);
    const hasMore = endIndex < allSchools.length;
    
    // Cache the result
    stateSchoolsCache[cacheKey] = {
      timestamp: Date.now(),
      schools: paginatedSchools,
      hasMore,
      totalCount: allSchools.length
    };
    
    // Also cache the first page for reference
    if (page === 0 || !isCacheValid(firstPageCacheKey, cacheExpiration)) {
      stateSchoolsCache[firstPageCacheKey] = {
        timestamp: Date.now(),
        schools: allSchools.slice(0, pageSize),
        hasMore: pageSize < allSchools.length,
        totalCount: allSchools.length
      };
    }
    
    return { 
      schools: paginatedSchools,
      hasMore,
      totalCount: allSchools.length
    };
  } catch (error) {
    console.error("Error in fetchPaginatedDogSchoolsByState:", error);
    console.log("No schools found in database for state, falling back to local data");
    
    // Fallback to local data
    // Normalize country code to full name for filtering local data
    const normalizedCountry = country === 'de' ? 'Germany' : 
                            country === 'at' ? 'Austria' : 
                            country === 'ch' ? 'Switzerland' : country;
    
    // Improved matching logic for state name variations
    const filteredSchools = dogSchools.filter(school => {
      const countryMatches = 
        school.country?.toLowerCase() === normalizedCountry.toLowerCase() ||
        (normalizedCountry.toLowerCase() === "germany" && school.country === "Germany") ||
        (normalizedCountry.toLowerCase() === "austria" && school.country === "Austria") ||
        (normalizedCountry.toLowerCase() === "switzerland" && school.country === "Switzerland");
      
      if (!countryMatches) return false;
      
      // Enhanced state name matching
      const schoolState = (school.state || "").toLowerCase().trim();
      const stateMatches = 
        schoolState === normalizedState ||
        schoolState.includes(normalizedState) ||
        normalizedState.includes(schoolState) ||
        schoolState.replace(/-/g, ' ') === normalizedState ||
        normalizedState.replace(/ /g, '-') === schoolState;
      
      return stateMatches;
    });
    
    // Sort schools by review count
    const sortedSchools = sortSchoolsByReviewCount(filteredSchools);
    
    // Apply pagination
    const startIndex = page * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedSchools = sortedSchools.slice(startIndex, endIndex);
    const hasMore = endIndex < sortedSchools.length;
    
    // Cache the result
    stateSchoolsCache[cacheKey] = {
      timestamp: Date.now(),
      schools: paginatedSchools,
      hasMore,
      totalCount: sortedSchools.length
    };
    
    return {
      schools: paginatedSchools,
      hasMore,
      totalCount: sortedSchools.length
    };
  }
};

// Global cache for paginated dog schools
const paginatedSchoolsCache: Record<string, {
  timestamp: number;
  schools: DogSchool[];
  hasMore: boolean;
  totalCount?: number;
}> = {};

/**
 * Optimized function to fetch paginated dog schools
 */
export const fetchPaginatedDogSchools = async (
  page: number = 0,
  pageSize: number = 30, // Reduce default page size for faster initial loading
  filters: FilterParams = {}
): Promise<{
  schools: DogSchool[];
  hasMore: boolean;
  totalCount?: number;
}> => {
  // Create a cache key based on filters
  const filterString = Object.entries(filters)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([key, value]) => `${key}:${value}`)
    .join('|');
  
  const cacheKey = `page-${page}-size-${pageSize}-filters-${filterString}`;
  
  // Check cache first
  if (
    paginatedSchoolsCache[cacheKey] &&
    Date.now() - paginatedSchoolsCache[cacheKey].timestamp < 5 * 60 * 1000 // 5 minute cache
  ) {
    console.log("Using cached paginated schools");
    return {
      schools: paginatedSchoolsCache[cacheKey].schools,
      hasMore: paginatedSchoolsCache[cacheKey].hasMore,
      totalCount: paginatedSchoolsCache[cacheKey].totalCount
    };
  }

  let query = supabase
    .from('hundeschuleList')
    .select('*', { count: 'exact' })
    .order('reviewscount', { ascending: false });

  // Apply filters more efficiently
  if (filters.country) {
    query = query.ilike('country', `%${filters.country}%`);
  }
  if (filters.state) {
    query = query.ilike('state', `%${filters.state}%`);
  }

  const startIndex = page * pageSize;
  const endIndex = startIndex + pageSize - 1; // Adjust endIndex calculation

  query = query.range(startIndex, endIndex);

  try {
    const { data, error, count } = await query;

    if (error) {
      console.error("Error fetching paginated dog schools:", error);
      throw error;
    }

    const schools: DogSchool[] = data
      ? data.map(row => mapSupabaseRowToDogSchool(row))
      : [];
    const hasMore = data ? data.length === pageSize : false;
    const totalCount = count !== null ? count : undefined;

    // Cache the results
    paginatedSchoolsCache[cacheKey] = {
      timestamp: Date.now(),
      schools,
      hasMore,
      totalCount
    };

    return { schools, hasMore, totalCount };
  } catch (error) {
    console.error("Error in fetchPaginatedDogSchools:", error);
    throw error;
  }
};
